import type { LucideIcon } from 'lucide-react'
import {
  Check,
  ChevronRight,
  ClipboardCheck,
  Lock,
  LogOut,
  Settings
} from 'lucide-react'
import { Link, useLocation } from 'react-router'

import { SidebarUserRow } from '@/components/sidebar-user-row'
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible'
import {
  Sidebar,
  SidebarAutoclose,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarTrigger
} from '@/components/ui/sidebar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { SidebarQuickNav } from '@/domains/app-shell'
import { useUser } from '@/domains/auth'
import { SidebarCompanyHeader } from '@/domains/companies'
import {
  type ChecklistItem,
  type SectionProgress,
  getSharedSteps,
  getSupplierSections
} from '@/domains/onboarding'

import { useSetupState } from './use-setup-state'

/* the sidebar's own tooltip prop is hidden unless the rail is icon-collapsed,
 * so a locked row needs its own to explain itself in the expanded sidebar. */
function LockedTooltip({
  item,
  fallback,
  children
}: {
  item: ChecklistItem
  fallback: string
  children: React.ReactNode
}) {
  return (
    <Tooltip>
      {/* the span is the trigger, not the row: both sidebar button variants
          set aria-disabled:pointer-events-none, so a locked row never sees
          the pointer enter. */}
      <TooltipTrigger asChild>
        <span className='block w-full'>{children}</span>
      </TooltipTrigger>
      <TooltipContent side='right'>
        {item.lockedReason ?? fallback}
      </TooltipContent>
      {/* touch has no hover, so the reason is printed under the row. */}
      <span className='block px-2 pb-1 text-[11px] text-sidebar-foreground/60 md:hidden'>
        {item.lockedReason ?? fallback}
      </span>
    </Tooltip>
  )
}

function OnboardingSectionNav({
  section,
  currentPath
}: {
  section: SectionProgress
  currentPath: string
}) {
  const Icon = section.info.icon
  const containsActive =
    currentPath === section.info.href ||
    section.items.some(item => currentPath === item.ctaHref)

  /* a section holding one step is not a section: expanding it reveals a child
   * with the same name, and the count can only ever read 0/1 or 1/1. */
  const only = section.items.length === 1 ? section.items[0] : null
  if (only) {
    return (
      <OnboardingFlatSection
        icon={Icon}
        title={section.info.title}
        item={only}
        active={containsActive}
      />
    )
  }

  return (
    <Collapsible asChild open={containsActive} className='group/section'>
      <SidebarMenuItem>
        <SidebarMenuButton
          asChild
          tooltip={section.info.title}
          isActive={currentPath === section.info.href}
        >
          <Link to={section.info.href}>
            <Icon />
            <span>{section.info.title}</span>
            <span className='ml-auto mr-1 text-[10px] text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden'>
              {section.complete}/{section.total}
            </span>
            <ChevronRight className='size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]/section:rotate-90 group-data-[collapsible=icon]:hidden' />
          </Link>
        </SidebarMenuButton>
        <CollapsibleContent>
          <SidebarMenuSub>
            {section.items.map(item => (
              <OnboardingSubStep
                key={item.id}
                item={item}
                active={currentPath === item.ctaHref}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}

function OnboardingFlatSection({
  icon: Icon,
  title,
  item,
  active
}: {
  icon: LucideIcon
  title: string
  item: ChecklistItem
  active: boolean
}) {
  if (item.disabled) {
    return (
      <SidebarMenuItem>
        <LockedTooltip item={item} fallback={title}>
          <SidebarMenuButton
            aria-disabled='true'
            tabIndex={-1}
            className='cursor-not-allowed opacity-50 hover:bg-transparent'
          >
            <Icon />
            <span>{title}</span>
            {item.complete ? (
              <Check className='ml-auto size-3 shrink-0 text-accent group-data-[collapsible=icon]:hidden dark:text-accent-mid' />
            ) : (
              <Lock className='ml-auto size-3 shrink-0 group-data-[collapsible=icon]:hidden' />
            )}
          </SidebarMenuButton>
        </LockedTooltip>
      </SidebarMenuItem>
    )
  }
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild tooltip={title} isActive={active}>
        <Link to={item.ctaHref}>
          <Icon />
          <span>{title}</span>
          {item.complete && (
            <Check className='ml-auto size-3 shrink-0 text-accent group-data-[collapsible=icon]:hidden dark:text-accent-mid' />
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}

function OnboardingSubStep({
  item,
  active
}: {
  item: ChecklistItem
  active: boolean
}) {
  if (item.disabled) {
    return (
      <SidebarMenuSubItem>
        <LockedTooltip item={item} fallback={item.title}>
          <SidebarMenuSubButton
            aria-disabled='true'
            tabIndex={-1}
            className='cursor-not-allowed opacity-50 hover:bg-transparent'
          >
            <span>{item.title}</span>
            <Lock className='ml-auto size-3 shrink-0' />
          </SidebarMenuSubButton>
        </LockedTooltip>
      </SidebarMenuSubItem>
    )
  }
  return (
    <SidebarMenuSubItem>
      <SidebarMenuSubButton asChild isActive={active}>
        <Link to={item.ctaHref}>
          <span>{item.title}</span>
          {item.complete && (
            <Check className='ml-auto size-3 shrink-0 text-accent dark:text-accent-mid' />
          )}
        </Link>
      </SidebarMenuSubButton>
    </SidebarMenuSubItem>
  )
}

export function OnboardingShell({
  children,
  title = 'Onboarding'
}: {
  children: React.ReactNode
  title?: React.ReactNode
}) {
  const { user, logout } = useUser()
  const { setupState } = useSetupState()
  const location = useLocation()
  const isOnboarding = setupState?.isOnboarding ?? true
  const sharedSteps: ChecklistItem[] = setupState
    ? getSharedSteps(setupState)
    : []
  const onboardingSections: SectionProgress[] = setupState?.isSupplier
    ? getSupplierSections(setupState)
    : []

  function isActive(href: string) {
    return (
      location.pathname === href || location.pathname.startsWith(`${href}/`)
    )
  }

  return (
    <TooltipProvider delayDuration={0}>
      <SidebarProvider>
        <SidebarAutoclose />
        <Sidebar collapsible='icon'>
          <SidebarHeader>
            <SidebarCompanyHeader
              homeHref={isOnboarding ? '/onboarding' : '/suppliers'}
            />
            <SidebarQuickNav />
          </SidebarHeader>

          <SidebarContent>
            {isOnboarding && (
              <SidebarGroup>
                <SidebarGroupLabel>Onboarding</SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === '/onboarding'}
                      >
                        <Link to='/onboarding'>
                          <ClipboardCheck />
                          <span>Overview</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                    {/** shared company-level steps both roles fill, above
                          the role sections */}
                    {sharedSteps.map(step => (
                      <SidebarMenuItem key={step.id}>
                        <SidebarMenuButton
                          asChild
                          isActive={location.pathname === step.ctaHref}
                        >
                          <Link to={step.ctaHref}>
                            <step.icon />
                            <span>{step.title}</span>
                            {step.complete && (
                              <Check className='ml-auto size-3 shrink-0 text-accent dark:text-accent-mid' />
                            )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                    {onboardingSections.map(section => (
                      <OnboardingSectionNav
                        key={section.info.id}
                        section={section}
                        currentPath={location.pathname}
                      />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            )}

            <SidebarGroup className='mt-auto'>
              <SidebarGroupContent>
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={isActive('/settings')}>
                      <Link to='/settings'>
                        <Settings />
                        <span>Settings</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                  <SidebarMenuItem>
                    <SidebarMenuButton onClick={() => void logout()}>
                      <LogOut />
                      <span>Logout</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          </SidebarContent>

          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarUserRow name={user?.name} email={user?.email} />
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>

          <SidebarRail />
        </Sidebar>

        <SidebarInset>
          <header className='sticky top-0 z-20 flex min-h-14 items-center gap-2 border-b border-border bg-page px-4 pt-[env(safe-area-inset-top)]'>
            <SidebarTrigger className='-ml-1' />
            <span className='min-w-0 flex-1 truncate text-sm font-medium text-ink'>
              {title}
            </span>
          </header>
          <main className='flex-1 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6'>
            {children}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  )
}
